import React, { createContext, useContext, ReactNode } from 'react';
import { useSettings, type Language } from '@/contexts/SettingsContext';
import { resolveTranslation } from '@/i18n/resolve';

/**
 * Global translation provider. The coreTranslations dictionary that used to
 * live inline here now lives in i18n/coreTranslations.ts, and the resolution
 * logic lives in i18n/resolve.ts. Both this provider and the lightweight
 * useTranslation hook delegate to the SAME resolver, so they can never diverge.
 */

interface GlobalTranslationContextType {
  t: (key: string, fallback?: string) => string;
  language: string;
  setLanguage: (lang: string) => void;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
}

const GlobalTranslationContext = createContext<GlobalTranslationContextType | undefined>(undefined);

/**
 * Global translation provider that wraps the entire app
 * Provides optimized translation functions for all components
 */
export function GlobalTranslationProvider({ children }: { children: ReactNode }) {
  const { settings, updateSettings } = useSettings();

  const setLanguage = (lang: string) => {
    updateSettings({ language: lang as Language });
  };

  // Delegates to the unified resolver (core → portal → demo → general → fallback
  // → key). The lightweight useTranslation hook delegates to the same function,
  // so the two hooks can never diverge again.
  const t = (key: string, fallback?: string): string =>
    resolveTranslation(key, settings.language, fallback);

  const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
    const localeMap: Record<string, string> = {
      'en': 'en-US',
      'es': 'es-ES',
      'jp': 'ja-JP',
      'fr-ca': 'fr-CA',
      'pt-br': 'pt-BR',
      'de': 'de-DE',
      'zh': 'zh-CN'
    };
    
    const locale = localeMap[settings.language] || 'en-US';
    return new Intl.NumberFormat(locale, options).format(value);
  };

  const formatCurrency = (value: number, currency: string = 'USD'): string => {
    return formatNumber(value, {
      style: 'currency',
      currency: currency
    });
  };

  return (
    <GlobalTranslationContext.Provider value={{
      t,
      language: settings.language,
      setLanguage,
      formatNumber,
      formatCurrency
    }}>
      {children}
    </GlobalTranslationContext.Provider>
  );
}

/**
 * Hook to use global translations
 * Performance-optimized for frequent use
 */
export function useGlobalTranslation() {
  const context = useContext(GlobalTranslationContext);
  if (context === undefined) {
    throw new Error('useGlobalTranslation must be used within a GlobalTranslationProvider');
  }
  return context;
}
